import moment from "moment";
import * as XLSX from "xlsx-js-style";

/**
 * Generates and downloads an Excel report for customers and their loans.
 *
 * @param {Array} customersData  - Array of customer objects (with a `loans` array of { LoanId })
 * @param {Array} loansData      - Array of loan objects (fetched from API, keyed by loanId)
 */
export const generateCustomerLoanExcel = (customersData, loansData) => {

  function getLoanEndDate(startDate, tenureType, noOfTenure) {
  const endDate = moment(startDate); // moment handles ISO 8601 natively

  if (!endDate.isValid()) return "N/A";

  switch (tenureType) {
    case "onetime":   break;
    case "daily":     endDate.add(noOfTenure - 1, "days");   break;
    case "weekly":    endDate.add(noOfTenure - 1, "weeks");  break;
    case "biweekly":  endDate.add((noOfTenure - 1) * 2, "weeks"); break;
    case "monthly":   endDate.add(noOfTenure - 1, "months"); break;
    case "ten_daily": endDate.add((noOfTenure - 1) * 10, "days"); break;
    default: return "Invalid tenure type";
  }

  return endDate.format("ll");
}

  // ─── Style helpers ───────────────────────────────────────────────────────────
  const HEADER_STYLE = {
    font: { bold: true, sz: 11, color: { rgb: "FFFFFF" }, name: "Arial" },
    fill: { fgColor: { rgb: "1F4E79" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: {
      top: { style: "thin", color: { rgb: "FFFFFF" } },
      bottom: { style: "thin", color: { rgb: "FFFFFF" } },
      left: { style: "thin", color: { rgb: "FFFFFF" } },
      right: { style: "thin", color: { rgb: "FFFFFF" } },
    },
  };

  const CELL_STYLE_EVEN = {
    font: { sz: 10, name: "Arial" },
    alignment: { horizontal: "center", vertical: "center" },
    fill: { fgColor: { rgb: "D6E4F0" } },
    border: {
      top: { style: "thin", color: { rgb: "BDD7EE" } },
      bottom: { style: "thin", color: { rgb: "BDD7EE" } },
      left: { style: "thin", color: { rgb: "BDD7EE" } },
      right: { style: "thin", color: { rgb: "BDD7EE" } },
    },
  };

  const CELL_STYLE_ODD = {
    ...CELL_STYLE_EVEN,
    fill: { fgColor: { rgb: "FFFFFF" } },
  };

  const STATUS_STYLE = (status) => ({
    font: {
      sz: 10,
      bold: true,
      name: "Arial",
      color: {
        rgb:
          status === "Closed"
            ? "1D6A1D"
            : status === "Open"
              ? "7B3F00"
              : "333333",
      },
    },
    alignment: { horizontal: "center", vertical: "center" },
    fill: {
      fgColor: {
        rgb:
          status === "Closed"
            ? "C6EFCE"
            : status === "Open"
              ? "FFEB9C"
              : "F2F2F2",
      },
    },
    border: CELL_STYLE_EVEN.border,
  });

  // ─── Build a fast lookup: loanId → loan object ───────────────────────────────
  const loanMap = {};
  (loansData || []).forEach((loan) => {
    loanMap[loan.loanId] = loan;
  });

  // ─── Column definitions ───────────────────────────────────────────────────────
  const COLUMNS = [
    { header: "First Name", key: "firstName", wch: 14 },
    { header: "Last Name", key: "lastName", wch: 14 },
    { header: "Branch", key: "branch", wch: 16 },
    { header: "Phone", key: "phone", wch: 16 },
    { header: "Cust ID", key: "cust_id", wch: 10 },
    { header: "Loan ID", key: "loanId", wch: 12 },
    { header: "Loan Type", key: "loanType", wch: 18 },
    { header: "Loan Start Date", key: "loanStartDate", wch: 14 },
    { header: "Loan End Date", key: "loanEndDate", wch: 14 },
    { header: "Loan Status", key: "loanStatus", wch: 13 },
    { header: "Loan Amount", key: "loanAmount", wch: 14 },
    { header: "Fees", key: "processFee", wch: 10 },
    { header: "Repayable Amount", key: "totalAmountToCollect", wch: 18 },
    { header: "Amount Collected", key: "amountCollected", wch: 18 },
    { header: "Arrear Amount", key: "arrearAmount", wch: 18 },
    { header: "Pending Dues Count", key: "pendingCount", wch: 20 },
    { header: "Outstanding Balance", key: "outstandingBalance", wch: 22 },
    { header: "Collection Agent", key: "collectorId", wch: 18 },
  ];

  // ─── Build worksheet rows ─────────────────────────────────────────────────────
  const wsData = [];

  // Header row
  wsData.push(COLUMNS.map((col) => ({ v: col.header, s: HEADER_STYLE })));

  // Data rows — one row per (customer × loan)
  let dataRowIndex = 0;

  (customersData || []).forEach((customer) => {
    const customerLoans = customer.loans || [];

    if (customerLoans.length === 0) {
      // Customer with no loans — still emit one row
      customerLoans.push(null);
    }

    // Sort: Closed loans first, then Open, then anything else
    const STATUS_ORDER = { Closed: 0, Open: 1 };

    customerLoans.sort((a, b) => {
      const loanA = loanMap[a?.LoanId ?? a?.loanId];
      const loanB = loanMap[b?.LoanId ?? b?.loanId];

      const statusA = loanA?.loanStatus ?? "";
      const statusB = loanB?.loanStatus ?? "";

      return (STATUS_ORDER[statusA] ?? 99) - (STATUS_ORDER[statusB] ?? 99);
    });

    customerLoans.forEach((loanRef) => {
      const loanId = loanRef?.LoanId ?? loanRef?.loanId ?? null;
      const loan = loanId ? loanMap[loanId] : null;

      // Derived financial metrics
      const totalRepayable = parseFloat(loan?.totalAmountToCollect ?? 0);
      const amountCollected = (loan?.loanCollections ?? []).reduce(
        (sum, c) => sum + parseFloat(c.collectionAmount ?? 0),
        0,
      );
      const pendingDues = loan?.pendingCount ?? 0;
      const outstandingBal = Math.max(0, totalRepayable - amountCollected);

      const rowStyle =
        dataRowIndex % 2 === 0 ? CELL_STYLE_EVEN : CELL_STYLE_ODD;
      const loanStatus = loan?.loanStatus ?? "";
      const caName =
        [loan?.user?.firstName, loan?.user?.lastName]?.filter(Boolean)?.join(' ') || "";

      const row = [
        { v: customer.firstName ?? "", s: rowStyle },
        { v: customer.lastName ?? "", s: rowStyle },
        { v: customer.branch?.branchName ?? "", s: rowStyle },
        { v: customer.phone ?? "", s: rowStyle },
        { v: customer.cust_id ?? "", s: rowStyle },
        { v: loanId ?? "N/A", s: rowStyle },
        { v: loan?.loanType?.loanType ?? "", s: rowStyle },
        { v: moment(loan?.startDate).format("ll") || "", s: rowStyle },
        { v: getLoanEndDate(loan?.startDate, loan?.loanTenureType, Number(loan?.loanTenure)) || "", s: rowStyle },
        { v: loanStatus, s: STATUS_STYLE(loanStatus) },
        { v: loan?.loanAmount ?? "", s: rowStyle },
        { v: parseFloat(loan?.processFee ?? 0), s: rowStyle },
        { v: totalRepayable, s: rowStyle },
        { v: amountCollected, s: rowStyle },
        { v: parseFloat(loan?.arrearAmount ?? 0), s: rowStyle },   // was: loan?.arrearAmount (undefined-prone)
        { v: pendingDues, s: rowStyle },
        { v: outstandingBal, s: rowStyle },
        { v: caName, s: rowStyle },                                 // was: caName || null
      ];

      wsData.push(row);
      dataRowIndex++;
    });
  });

  // ─── Create worksheet ─────────────────────────────────────────────────────────
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Column widths
  ws["!cols"] = COLUMNS.map((col) => ({ wch: col.wch }));

  // Freeze the header row
  // ws["!freeze"] = { xSplit: 0, ySplit: 1 };
  ws["!sheetViews"] = [{ state: "frozen", ySplit: 1, xSplit: 0, topLeftCell: "A2", activeCell: "A2" }];

  // Row height for header
  ws["!rows"] = [{ hpt: 30 }]; // header row height in points

  // ─── Create workbook & download ───────────────────────────────────────────────
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Customer Loan Report");

  const timestamp = new Date()
    .toLocaleString("sv-SE", { timeZone: "Asia/Kolkata" })
    .replace(/[- :]/g, "")
    .slice(0, 14);

  XLSX.writeFile(wb, `BorrowersData_${timestamp}.xlsx`);
};
